<template>
  <button
    type="button"
    :disabled="isLoading"
    class="btn btn--primary"
    @click="handleClick"
  >
    <span class="relative inline-flex items-center justify-center">
      <BaseLoadingIcon :visible="isLoading" />
      <span
        class="whitespace-nowrap"
        :class="{ 'invisible': isLoading }"
      >
        {{ buttonText }}
      </span>
    </span>
  </button>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { log } from '@js/utils/logger'
import { authLinks, checkoutLinks } from '@js/utils/links'
import { trackEvent } from '@js/utils/trackEvent'

import useAuth from '@js/composables/useAuth'
import useCart from '@js/composables/useCart'

import { useUserStore } from '@js/stores/userStore'

import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'

defineOptions({
  name: 'AppRegisterButton',
})

const props = withDefaults(
  defineProps<{
    sku: string
    buttonText?: string
    courseOption: string
    craftEntryId: number
    registrationType?: 'course' | 'webinar'
  }>(),
  {
    buttonText: 'Register',
  },
)

const { addItemToCart } = useCart()
const { isLoggedIn, setLastRegistration } = useAuth()
const userStore = useUserStore()

const isLoading = ref(false)

/**
 * Handles adding the item to the cart.
 */
const handleAddItem = async (): Promise<void> => {
  isLoading.value = true

  const result = await addItemToCart(props.sku, props.courseOption, props.craftEntryId)

  if (result.status === 'success') {
    const registration = result.data

    setLastRegistration(registration.registration_id)

    // Update incomplete registrations count in the global user store
    userStore.incrementIncompleteRegistrationsCount()

    // Track the add to cart event in GTM
    trackEvent({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_id: registration.item.sku,
            item_category: registration.item.course_option,
            quantity: 1,
          }
        ]
      }
    })

    emitGlobalSuccess()
    window.location.assign(checkoutLinks.CHECKOUT)
  } else {
    const dashboardLink ='<a href="/account/dashboard" class="underline hover:no-underline">View Dashboard</a>'

    // Display specific error if user is already enrolled
    if (result.data === 'You are already enrolled in this class.') {
      emitGlobalError(
        props.registrationType === 'webinar'
          ? `Can not register, You are already enrolled. ${dashboardLink}`
          : `You are already enrolled in this class. ${dashboardLink}`
      )
    } else {
      emitGlobalError(
        `There was an error registering for this ${props.registrationType}. Please reach out to <a href="mailto:coursesupport@relias.com" class="underline hover:no-underline">coursesupport@relias.com</a>`
      )
    }

    log({
      message: 'Item could not be added to the cart',
      file: 'src/wcei/js/components/checkout/AppRegisterButton.vue',
    })
  }

  isLoading.value = false
}

const handleClick = async (): Promise<void> => {
  if (isLoggedIn.value) {
    await handleAddItem()
  } else {
    // Track login page hits for course registration
    trackEvent({
      event: 'course_registration_login',
    })

    window.location.assign(`${authLinks.LOGIN}?sku=${props.sku}&course_option=${props.courseOption}&craft_entry_id=${props.craftEntryId}`)
  }
}

/**
 * Handles emitting a global error event when there is an issue adding a course to the cart.
 */
const emitGlobalError = (message: string) => {
  window.dispatchEvent(new CustomEvent('register:addToCartError', {
    detail: { message },
  }))
}

/**
 * Handles emitting a global success event when a course is added to the cart.
 */
const emitGlobalSuccess = () => {
  window.dispatchEvent(new CustomEvent('register:addToCartSuccess'))
}


</script>
